// =============================================================================
// PEDRA — src/core/validation.ts
// Regras de validacao com mensagens ja em pt-BR, prontas para exibir.
// Nenhuma funcao aqui lanca excecao: todas devolvem listas de mensagens.
// =============================================================================

import type { Micron, Part, Project, Settings, StockSheet, Unit } from './types';
import {
  DEFAULT_DECIMALS,
  MICRON_PER_UNIT,
  UNIT_NOUN,
  areaOf,
  formatArea,
  formatDimensions,
  formatLength,
  isImperial,
} from './units';

// -----------------------------------------------------------------------------
// LIMITES GLOBAIS
// -----------------------------------------------------------------------------

export const LIMITS = {
  /** Menor dimensao aceita: 1 µm. */
  MIN_DIMENSION: 1 as Micron,
  /** Maior dimensao aceita: 100 m. */
  MAX_DIMENSION: 100_000_000 as Micron,
  /** Maior kerf aceito: 50 mm. */
  MAX_KERF: 50_000 as Micron,
  /** Kerf acima disto gera aviso (mas nao erro). */
  WARN_KERF: 12_000 as Micron,
  MAX_QTY: 10_000,
  MAX_ROWS: 5_000,
  MAX_TOTAL_INSTANCES: 20_000,
  MAX_LABEL_LENGTH: 120,
  MAX_NAME_LENGTH: 80,
  /**
   * Menor lado plausivel para a MAIOR chapa do projeto: 50 mm.
   * Abaixo disso o projeto inteiro caberia na palma da mao — possivel (joalheria,
   * miniatura), mas muito mais provavel que a unidade esteja errada.
   * Limiar generoso de proposito: so vira PERGUNTA, nunca acusacao, e nunca
   * bloqueia o calculo.
   */
  MIN_PLAUSIBLE_SHEET: 50_000 as Micron,
  /** Maior lado plausivel para a maior chapa: 50 m. Acima disso, idem. */
  MAX_PLAUSIBLE_SHEET: 50_000_000 as Micron,
} as const;

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** true se a cor esta no formato #RRGGBB. */
export function isValidHexColor(color: string): boolean {
  return HEX_COLOR.test(color);
}

function isPositiveIntegerLike(value: number): boolean {
  return Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

/** `formatLength` com as configuracoes do projeto. */
function len(v: Micron, s: Settings): string {
  return formatLength(v, s.unit, s.decimalPlaces);
}

/** Descricao curta da peca para as mensagens. */
export function describePart(p: Part, index?: number): string {
  const name = p.label.trim() !== '' ? `"${p.label.trim()}"` : 'sem nome';
  return index === undefined ? `A peça ${name}` : `A peça ${name} (linha ${index + 1})`;
}

/** Descricao curta da chapa para as mensagens. */
export function describeStock(s: StockSheet, index?: number): string {
  const name = s.label.trim() !== '' ? `"${s.label.trim()}"` : 'sem nome';
  return index === undefined ? `A chapa ${name}` : `A chapa ${name} (linha ${index + 1})`;
}

/**
 * Orientacoes em que a peca pode ser colocada nesta chapa.
 * REGRA OFICIAL DO VEIO — o otimizador DEVE usar exatamente esta:
 *   - veio desligado, ou peca sem veio (0), ou chapa sem veio (0) -> as duas;
 *   - veios iguais    -> apenas 'normal';
 *   - veios distintos -> apenas 'rotated'.
 */
export function allowedOrientations(
  part: Pick<Part, 'orientation'>,
  sheet: Pick<StockSheet, 'orientation'>,
  considerGrain: boolean,
): ReadonlyArray<'normal' | 'rotated'> {
  if (!considerGrain || part.orientation === 0 || sheet.orientation === 0) {
    return ['normal', 'rotated'];
  }
  return part.orientation === sheet.orientation ? ['normal'] : ['rotated'];
}

/** true se a peca cabe na chapa em ALGUMA orientacao permitida. */
export function partFitsSheet(part: Part, sheet: StockSheet, considerGrain: boolean): boolean {
  for (const o of allowedOrientations(part, sheet, considerGrain)) {
    if (o === 'normal' && part.width <= sheet.width && part.length <= sheet.length) return true;
    if (o === 'rotated' && part.length <= sheet.width && part.width <= sheet.length) return true;
  }
  return false;
}

/** true se a peca caberia ignorando a restricao de veio. */
function partFitsIgnoringGrain(part: Part, sheet: StockSheet): boolean {
  return (
    (part.width <= sheet.width && part.length <= sheet.length) ||
    (part.length <= sheet.width && part.width <= sheet.length)
  );
}

/**
 * true quando a linha nunca foi preenchida (a grade sempre mantem uma linha
 * vazia no fim de cada tabela). Cobrar medidas de uma linha em branco seria
 * inventar um problema que o usuario nao criou: a ausencia de dados ja e
 * relatada uma unica vez por "Nenhuma peça/chapa pronta...".
 */
function isUntouchedRow(r: {
  length: number;
  width: number;
  label: string;
  material: string;
}): boolean {
  return r.length === 0 && r.width === 0 && r.label.trim() === '' && r.material.trim() === '';
}

/** Chapas que valem para esta peca considerando (ou nao) o material. */
function candidateSheets(part: Part, stock: StockSheet[], settings: Settings): StockSheet[] {
  const enabled = stock.filter((s) => s.enabled && s.width > 0 && s.length > 0);
  if (!settings.considerMaterial) return enabled;
  return enabled.filter((s) => s.material.trim() === part.material.trim());
}

// -----------------------------------------------------------------------------
// ESCALA IMPROVAVEL (UNIDADE PROVAVELMENTE ERRADA)
// -----------------------------------------------------------------------------

/**
 * Suspeita de unidade trocada, pronta para virar aviso na tela.
 *
 * NASCEU DE UM CASO REAL: o usuario digitou 3,07 e 1,9 para a chapa achando
 * que estava em metros, mas o navegador tinha a unidade em MILIMETRO guardada
 * de outra sessao. O app obedeceu — chapa de 3,07 mm, 1,5% de aproveitamento,
 * 106 peças sem lugar — e nunca disse em que unidade ele estava digitando.
 *
 * `suggested` fica exposto (e nao so a frase) para a interface poder oferecer
 * um botao que TROCA a unidade em um clique.
 */
export interface ScaleSuspicion {
  /** Unidade em uso agora. */
  readonly unit: Unit;
  /** Unidade que os numeros sugerem. */
  readonly suggested: Unit;
  /** Frase pronta em pt-BR, em forma de pergunta. */
  readonly message: string;
}

/** O mesmo NUMERO digitado, relido na outra unidade. `3,07 mm` -> `3,07 m`. */
function reread(v: Micron, from: Unit, to: Unit): Micron {
  return Math.round((v * MICRON_PER_UNIT[to]) / MICRON_PER_UNIT[from]);
}

/**
 * Detecta medidas implausiveis para a unidade ativa e sugere a provavel.
 * Devolve `null` quando nao ha motivo para desconfiar — que e o caso comum.
 *
 * So olha CHAPAS, e so a maior delas: peca miuda em chapa normal e rotina de
 * marcenaria, chapa miuda e que e sinal de escala errada. Unidades imperiais
 * ficam de fora: `2,5"` e `120"` sao ambos corriqueiros, nao ha faixa
 * implausivel que valha o incomodo de um falso alarme.
 */
export function detectScaleMismatch(
  stock: readonly StockSheet[],
  settings: Settings,
): ScaleSuspicion | null {
  const unit = settings.unit;
  if (isImperial(unit)) return null;

  const usable = stock.filter((s) => s.enabled && s.width > 0 && s.length > 0);
  if (usable.length === 0) return null;

  let biggest = usable[0] as StockSheet;
  for (const s of usable) {
    if (Math.max(s.length, s.width) > Math.max(biggest.length, biggest.width)) biggest = s;
  }
  const side = Math.max(biggest.length, biggest.width);

  let suggested: Unit | null = null;
  let why = '';
  if (side < LIMITS.MIN_PLAUSIBLE_SHEET) {
    // Numeros pequenos demais: quem digita "3,07" quase sempre quer metros.
    suggested = unit === 'm' ? null : 'm';
    why = `parecem pequenas demais para ${UNIT_NOUN[unit]}`;
  } else if (unit === 'm' && side > LIMITS.MAX_PLAUSIBLE_SHEET) {
    suggested = 'mm';
    why = `parecem grandes demais para ${UNIT_NOUN[unit]}`;
  }
  if (suggested === null) return null;

  const dp = settings.decimalPlaces;
  const order = settings.dimensionOrder;
  const atual = formatDimensions(biggest.length, biggest.width, unit, dp, order);
  const outra = formatDimensions(
    reread(biggest.length, unit, suggested),
    reread(biggest.width, unit, suggested),
    suggested,
    DEFAULT_DECIMALS[suggested],
    order,
  );

  return {
    unit,
    suggested,
    message:
      `As medidas ${why}: a maior chapa está com ${atual}. ` +
      `Você quis dizer ${UNIT_NOUN[suggested]}? Nessa unidade a chapa ficaria ${outra}. ` +
      `Para trocar, abra Configurações → Medidas e exibição → Unidade. ` +
      `Se as medidas estiverem certas assim, é só seguir com o cálculo.`,
  };
}

// -----------------------------------------------------------------------------
// PECA
// -----------------------------------------------------------------------------

/** Valida uma peca isolada. Devolve as mensagens de erro em pt-BR. */
export function validatePart(p: Part, settings: Settings): string[] {
  const out: string[] = [];
  const who = describePart(p);

  if (!Number.isFinite(p.length) || p.length <= 0) {
    out.push(`${who} está sem comprimento. Informe uma medida maior que zero.`);
  } else if (p.length > LIMITS.MAX_DIMENSION) {
    out.push(
      `${who} tem comprimento acima do limite de ${len(LIMITS.MAX_DIMENSION, settings)}.`,
    );
  }

  if (!Number.isFinite(p.width) || p.width <= 0) {
    out.push(`${who} está sem largura. Informe uma medida maior que zero.`);
  } else if (p.width > LIMITS.MAX_DIMENSION) {
    out.push(`${who} tem largura acima do limite de ${len(LIMITS.MAX_DIMENSION, settings)}.`);
  }

  if (!isPositiveIntegerLike(p.qty)) {
    out.push(`${who} tem quantidade inválida. Use um número inteiro maior ou igual a zero.`);
  } else if (p.qty > LIMITS.MAX_QTY) {
    out.push(
      `${who} passa da quantidade máxima por linha (${LIMITS.MAX_QTY.toLocaleString('pt-BR')}).`,
    );
  }

  if (p.label.length > LIMITS.MAX_LABEL_LENGTH) {
    out.push(`${who} tem um nome longo demais (máximo ${LIMITS.MAX_LABEL_LENGTH} caracteres).`);
  }

  // Material so e obrigatorio quando o usuario LIGOU "Considerar material".
  if (settings.considerMaterial && p.material.trim() === '') {
    out.push(
      `${who} está sem material. Informe o material ou desligue "Considerar material" nas opções.`,
    );
  }

  if (p.color !== '' && !isValidHexColor(p.color)) {
    out.push(`${who} tem uma cor inválida. Use o formato #RRGGBB.`);
  }

  if (p.orientation !== 0 && p.orientation !== 1 && p.orientation !== 2) {
    out.push(`${who} tem uma direção de veio inválida.`);
  }

  return out;
}

// -----------------------------------------------------------------------------
// CHAPA
// -----------------------------------------------------------------------------

/** Valida uma chapa de estoque isolada. */
export function validateStock(s: StockSheet, settings: Settings): string[] {
  const out: string[] = [];
  const who = describeStock(s);

  if (!Number.isFinite(s.length) || s.length <= 0) {
    out.push(`${who} está sem comprimento. Informe uma medida maior que zero.`);
  } else if (s.length > LIMITS.MAX_DIMENSION) {
    out.push(`${who} tem comprimento acima do limite de ${len(LIMITS.MAX_DIMENSION, settings)}.`);
  }

  if (!Number.isFinite(s.width) || s.width <= 0) {
    out.push(`${who} está sem largura. Informe uma medida maior que zero.`);
  } else if (s.width > LIMITS.MAX_DIMENSION) {
    out.push(`${who} tem largura acima do limite de ${len(LIMITS.MAX_DIMENSION, settings)}.`);
  }

  if (s.qty !== null) {
    if (!isPositiveIntegerLike(s.qty)) {
      out.push(
        `${who} tem quantidade inválida. Use um número inteiro, ou deixe vazio para estoque ilimitado.`,
      );
    } else if (s.qty > LIMITS.MAX_QTY) {
      out.push(
        `${who} passa da quantidade máxima por linha (${LIMITS.MAX_QTY.toLocaleString('pt-BR')}).`,
      );
    }
  }

  if (s.label.length > LIMITS.MAX_LABEL_LENGTH) {
    out.push(`${who} tem um nome longo demais (máximo ${LIMITS.MAX_LABEL_LENGTH} caracteres).`);
  }

  // Material so e obrigatorio quando o usuario LIGOU "Considerar material".
  if (settings.considerMaterial && s.material.trim() === '') {
    out.push(
      `${who} está sem material. Informe o material ou desligue "Considerar material" nas opções.`,
    );
  }

  if (s.orientation !== 0 && s.orientation !== 1 && s.orientation !== 2) {
    out.push(`${who} tem uma direção de veio inválida.`);
  }

  return out;
}

// -----------------------------------------------------------------------------
// CONFIGURACOES
// -----------------------------------------------------------------------------

/** Valida as configuracoes. Devolve erros e avisos separados. */
export function validateSettings(s: Settings): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!Number.isFinite(s.kerf) || s.kerf < 0) {
    errors.push('A espessura de corte não pode ser negativa.');
  } else if (s.kerf > LIMITS.MAX_KERF) {
    errors.push(
      `A espessura de corte (${len(s.kerf, s)}) passa do limite de ${len(LIMITS.MAX_KERF, s)}.`,
    );
  } else if (s.kerf === 0) {
    warnings.push(
      'A espessura de corte está em zero: o plano não vai descontar o material removido pela serra.',
    );
  } else if (s.kerf > LIMITS.WARN_KERF) {
    warnings.push(
      `Espessura de corte de ${len(s.kerf, s)} é fora do comum. O usual fica entre 1 mm e 8 mm.`,
    );
  }

  if (!Number.isFinite(s.minTrimDimension) || s.minTrimDimension < 0) {
    errors.push('A dimensão mínima de aparo não pode ser negativa.');
  }

  if (!Number.isInteger(s.decimalPlaces) || s.decimalPlaces < 0 || s.decimalPlaces > 3) {
    errors.push('As casas decimais devem ser um número inteiro de 0 a 3.');
  }

  if (!Number.isFinite(s.timeBudgetMs) || s.timeBudgetMs < 200 || s.timeBudgetMs > 600_000) {
    errors.push('O tempo de cálculo deve ficar entre 0,2 e 600 segundos.');
  }

  if (!Number.isInteger(s.seed)) {
    errors.push('A semente de aleatoriedade deve ser um número inteiro.');
  }

  return { errors, warnings };
}

// -----------------------------------------------------------------------------
// PROJETO
// -----------------------------------------------------------------------------

/**
 * Valida o projeto inteiro, incluindo as regras cruzadas entre pecas e chapas.
 * Nunca lanca.
 *
 * FRONTEIRA ENTRE ERRO E AVISO — regra unica deste modulo:
 *  - ERRO   = o otimizador nao teria o que fazer (sem peca valida, sem chapa
 *             valida, nenhuma peca cabendo em lugar nenhum, opcao fora de
 *             faixa). Impede o calculo.
 *  - AVISO  = o calculo roda normalmente e o proprio resultado ja mostra a
 *             consequencia. Peca grande demais, material sem chapa ou veio
 *             conflitante NAO bloqueiam: elas saem na aba "Não couberam" com
 *             o motivo, exatamente como no CutList Optimizer original.
 */
export function validateProject(p: Project): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const settings = p.settings;

  // --- configuracoes ---------------------------------------------------------
  const cfg = validateSettings(settings);
  errors.push(...cfg.errors);
  warnings.push(...cfg.warnings);

  if (p.name.trim() === '') warnings.push('O projeto está sem nome.');
  if (p.name.length > LIMITS.MAX_NAME_LENGTH) {
    errors.push(`O nome do projeto é longo demais (máximo ${LIMITS.MAX_NAME_LENGTH} caracteres).`);
  }

  // --- tamanho das tabelas ---------------------------------------------------
  if (p.parts.length > LIMITS.MAX_ROWS) {
    errors.push(
      `A lista de peças tem ${p.parts.length.toLocaleString('pt-BR')} linhas. O limite é ${LIMITS.MAX_ROWS.toLocaleString('pt-BR')}.`,
    );
  }
  if (p.stock.length > LIMITS.MAX_ROWS) {
    errors.push(
      `A lista de chapas tem ${p.stock.length.toLocaleString('pt-BR')} linhas. O limite é ${LIMITS.MAX_ROWS.toLocaleString('pt-BR')}.`,
    );
  }

  // --- linhas individuais ----------------------------------------------------
  p.parts.forEach((part, i) => {
    if (!part.enabled || isUntouchedRow(part)) return;
    for (const msg of validatePart(part, settings)) {
      errors.push(msg.replace(describePart(part), describePart(part, i)));
    }
  });

  p.stock.forEach((sheet, i) => {
    if (!sheet.enabled || isUntouchedRow(sheet)) return;
    for (const msg of validateStock(sheet, settings)) {
      errors.push(msg.replace(describeStock(sheet), describeStock(sheet, i)));
    }
  });

  // --- fitas de borda --------------------------------------------------------
  if (settings.edgeBandingEnabled) {
    const bandIds = new Set(p.edgeBands.map((b) => b.id));
    p.parts.forEach((part, i) => {
      if (!part.enabled) return;
      const sides = [
        part.edgeBanding.top,
        part.edgeBanding.right,
        part.edgeBanding.bottom,
        part.edgeBanding.left,
      ];
      if (sides.some((id) => id !== null && !bandIds.has(id))) {
        errors.push(`${describePart(part, i)} usa uma fita de borda que não está cadastrada.`);
      }
    });
    for (const b of p.edgeBands) {
      if (b.name.trim() === '') errors.push('Há uma fita de borda sem nome no cadastro.');
      if (!Number.isFinite(b.thickness) || b.thickness <= 0) {
        errors.push(`A fita "${b.name || 'sem nome'}" está sem espessura.`);
      }
    }
  }

  // --- listas vazias ---------------------------------------------------------
  const activeParts = p.parts.filter((x) => x.enabled && x.qty > 0 && x.width > 0 && x.length > 0);
  const activeStock = p.stock.filter(
    (x) => x.enabled && (x.qty === null || x.qty > 0) && x.width > 0 && x.length > 0,
  );

  if (p.parts.length === 0) {
    errors.push('Adicione pelo menos uma peça para calcular o plano de corte.');
  } else if (activeParts.length === 0) {
    errors.push(
      'Nenhuma peça pronta para cortar. Informe comprimento, largura e quantidade de pelo menos uma peça ativa.',
    );
  }

  if (p.stock.length === 0) {
    errors.push('Adicione pelo menos uma chapa de estoque.');
  } else if (activeStock.length === 0) {
    errors.push(
      'Nenhuma chapa pronta para usar. Informe comprimento, largura e quantidade de pelo menos uma chapa ativa.',
    );
  }

  // --- escala improvavel -----------------------------------------------------
  // Vai na FRENTE da fila: e o unico aviso que fala do projeto inteiro estar
  // na escala errada, e a barra lateral so mostra os dois primeiros. Aviso, e
  // nao erro — quem faz miniatura tem medidas assim de verdade.
  const scale = detectScaleMismatch(p.stock, settings);
  if (scale !== null) warnings.unshift(scale.message);

  if (activeParts.length === 0 || activeStock.length === 0) {
    return { errors, warnings };
  }

  // --- total de instancias ---------------------------------------------------
  const totalInstances = activeParts.reduce((acc, x) => acc + x.qty, 0);
  if (totalInstances > LIMITS.MAX_TOTAL_INSTANCES) {
    errors.push(
      `O total de peças (${totalInstances.toLocaleString('pt-BR')}) passa do limite de ${LIMITS.MAX_TOTAL_INSTANCES.toLocaleString('pt-BR')}. Reduza as quantidades ou divida em vários projetos.`,
    );
  }

  // --- kerf versus menor peca ------------------------------------------------
  // Nunca bloqueia: o otimizador sabe lidar com kerf grande, e uma peca miuda
  // no meio de pecas normais nao pode impedir o plano das outras.
  let smallestSide = Infinity;
  for (const part of activeParts) smallestSide = Math.min(smallestSide, part.width, part.length);
  if (settings.kerf > 0 && smallestSide <= settings.kerf) {
    warnings.push(
      `A menor peça (${len(smallestSide, settings)}) é menor ou igual à espessura de corte (${len(settings.kerf, settings)}). Confira as medidas: peças desse tamanho podem não sobreviver ao corte.`,
    );
  } else if (settings.kerf > 0 && smallestSide < settings.kerf * 3) {
    warnings.push(
      `A menor peça (${len(smallestSide, settings)}) é muito pequena perto da espessura de corte (${len(settings.kerf, settings)}). Confira as medidas.`,
    );
  }

  // --- encaixe de cada peca --------------------------------------------------
  // Uma peca que nao cabe NAO impede o calculo: o otimizador corta todo o
  // resto e manda essa peca para "Não couberam" com o motivo. O calculo so e
  // barrado quando NENHUMA peca tem onde ser cortada.
  let placeable = 0;
  activeParts.forEach((part) => {
    const index = p.parts.indexOf(part);
    const who = describePart(part, index >= 0 ? index : undefined);
    const candidates = candidateSheets(part, p.stock, settings);

    if (candidates.some((s) => partFitsSheet(part, s, settings.considerGrain))) {
      placeable += 1;
      return;
    }

    if (candidates.length === 0) {
      warnings.push(
        `${who} vai para "Não couberam": não há chapa de "${part.material.trim() || 'sem material'}" no estoque. Cadastre uma chapa desse material ou mude o material da peça.`,
      );
      return;
    }

    if (candidates.some((s) => partFitsIgnoringGrain(part, s))) {
      warnings.push(
        `${who} vai para "Não couberam": só caberia girada 90°, e a direção do veio não permite. Mude o veio para "Indiferente" ou desligue "Considerar veio".`,
      );
      return;
    }

    let biggest = candidates[0] as StockSheet;
    for (const s of candidates) {
      if (areaOf(s.width, s.length) > areaOf(biggest.width, biggest.length)) biggest = s;
    }
    warnings.push(
      `${who} (${len(part.length, settings)} × ${len(part.width, settings)}) vai para "Não couberam": é maior que a maior chapa disponível (${len(biggest.length, settings)} × ${len(biggest.width, settings)}).`,
    );
  });

  if (placeable === 0) {
    errors.push(
      'Nenhuma peça cabe nas chapas cadastradas. Cadastre uma chapa maior ou reduza as medidas das peças.',
    );
  }

  // --- area total ------------------------------------------------------------
  const partsArea = activeParts.reduce((acc, x) => acc + areaOf(x.width, x.length) * x.qty, 0);
  const hasUnlimited = activeStock.some((s) => s.qty === null);
  if (hasUnlimited) {
    warnings.push(
      'Há chapas com estoque ilimitado: o otimizador vai usar quantas chapas forem necessárias.',
    );
  } else {
    const stockArea = activeStock.reduce(
      (acc, s) => acc + areaOf(s.width, s.length) * (s.qty ?? 0),
      0,
    );
    if (partsArea > stockArea) {
      warnings.push(
        `A área total das peças (${formatArea(partsArea, settings.unit)}) é maior que a área disponível em estoque (${formatArea(stockArea, settings.unit)}). Algumas peças não vão caber.`,
      );
    }
  }

  // --- rotulos duplicados ----------------------------------------------------
  if (settings.labelsOnPanels) {
    const seen = new Map<string, number>();
    for (const part of activeParts) {
      const label = part.label.trim();
      if (label === '') continue;
      seen.set(label, (seen.get(label) ?? 0) + 1);
    }
    for (const [label, count] of seen) {
      if (count > 1) {
        warnings.push(
          `Existem ${count} linhas de peça com o nome "${label}". É permitido, mas dificulta a leitura do plano.`,
        );
      }
    }
  }

  // --- materiais orfaos ------------------------------------------------------
  if (settings.considerMaterial) {
    const stockMaterials = new Set(activeStock.map((s) => s.material.trim()));
    const partMaterials = new Set(activeParts.map((x) => x.material.trim()));
    for (const m of stockMaterials) {
      if (m !== '' && !partMaterials.has(m)) {
        warnings.push(`Não há nenhuma peça usando o material "${m}".`);
      }
    }
  }

  // --- tempo estimado --------------------------------------------------------
  if (totalInstances > 2_000) {
    warnings.push(
      'Este cálculo pode demorar por causa do número de peças. Se ficar lento, reduza o tempo de cálculo nas opções.',
    );
  }

  return { errors, warnings };
}

/** true quando nao ha nenhum erro bloqueante. */
export function canOptimize(result: { errors: string[] }): boolean {
  return result.errors.length === 0;
}
